import React from 'react';
import { ServiceSelectionButton } from '@components/ui';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from '@/providers/AnalyticsProvider';
import { getConfig } from '@utils/config';
import { isMobile, isIOS, isAndroid } from '@utils/helpers';
import type { ServiceType } from '@/types';
import VisIcon from '@/assets/images/vis_icon.svg?react';
import SignIcon from '@/assets/images/sign_icon.svg?react';

export function ServiceSelectionPage() {
  const [selectedService, setSelectedService] = React.useState<ServiceType | null>(null);
  const { trackEvent } = useAnalytics();
  const navigate = useNavigate();
  const config = getConfig();

  const handleServiceClick = (service: ServiceType) => {
    setSelectedService(service);

    // apply a delay
    setTimeout(() => {
      // Track service selection
      trackEvent('service_selected', {
        service_type: service,
        is_mobile: isMobile(),
        platform: isIOS() ? 'ios' : isAndroid() ? 'android' : 'web',
        environment: config?.ENVIRONMENT || 'unknown',
      });
      // Preserve query params from current URL
      const queryString = window.location.search;
      const redirectLink = `/redirect/${service}${queryString}`;
      navigate(redirectLink);
    }, 500);
  };

  // Icon definitions using asset imports
  const visIcon = <VisIcon />;
  const slsIcon = <SignIcon />;

  return (
    <section
      className="min-h-dvh flex flex-col justify-center items-center px-4"
      aria-label="Service selection"
    >
      <div className="w-full max-w-[400px] flex flex-col items-start pl-2">
        <div className="mb-6 w-full">
          <img src="/images/logo.svg" alt="Aira logo" className="h-10 w-auto" />
        </div>
        <h1 className="text-left text-[1.45em] font-bold mb-9 leading-snug">
          What type of service are you seeking?
        </h1>
      </div>
      <div className="flex flex-col w-full max-w-[400px] items-start space-y-4">
        <ServiceSelectionButton
          selected={selectedService === 'vis'}
          onClick={() => handleServiceClick('vis')}
          title="Visual interpreting"
          subtitle="For blind and low vision"
          ariaLabel="For vision"
          icon={visIcon}
        />

        <ServiceSelectionButton
          selected={selectedService === 'sls'}
          onClick={() => handleServiceClick('sls')}
          title="Sign language interpreting"
          subtitle="For Deaf and hard of hearing"
          ariaLabel="For hearing"
          icon={slsIcon}
        />
      </div>
      <div className="justify-center max-w-[400px] mt-8 pt-6">
        <a
          href="https://aira.io/"
          target="_blank"
          className="text-[#00717B] underline text-base font-medium hover:no-underline hover:text-[#005a63]"
          rel="noreferrer"
        >
          Learn more about Aira
        </a>
      </div>
    </section>
  );
}
